import { ModelOptions } from './models'
import { MergedModel, OpenAICompletionMessage } from './msq'

export interface AgentEmbeddingOptions {
  model: string
  collections: string[]
  type: string
}

export interface AgentToolOptions {
  systemTools: string[]
  userTools: string[]
  selectedFunctions?: {
    [serverName: string]: string[] // Array of function names to include
  }
}

export interface PublishedAgentInfo {
  agentName: string;
  slug: string;
  isPublished: boolean;
  publishedAt?: number;
  publicUrl: string;
}

export interface SharedAgentInfo {
  agentId: string;
  agentName: string;
  icon?: string;
  slug: string;
  description: string;
  isPublished: boolean;
  publishedAt: number;
  ownerUsername: string;
  sharedAt: number;
  sharedVia: 'username' | 'email';
  hasVoice?: boolean;
  editable: boolean;
  modelName?: string;
  toolsCount?: number;
}

export interface AgentVoiceConfig {
  savedVoiceName: string
  responseFormat?: 'mp3' | 'opus' | 'aac' | 'flac' | 'wav' | 'pcm'
  speed?: number
  ttsOptions?: Record<string, unknown>
}

export interface AgentConfig {
  id: string
  timestamp: number
  name: string
  description: string
  icon?: string
  systemPrompt: string
  model: string // Name of the user-configured model (e.g., "my-gpt4")
  temperature: number
  maxTokens: number
  context: OpenAICompletionMessage[]
  sendContextToAgent?: string
  tools?: any
  hash?: string
  modelOptions: ModelOptions
  embeddingOptions?: AgentEmbeddingOptions
  toolOptions?: AgentToolOptions
  addToday?: boolean           // Optional flag to add today's date to the prompt
  timezoneOffset?: string      // Optional timezoneOffset for addToday (based on UTC)
  publishedInfo?: PublishedAgentInfo
  combineSystemPrompts?: boolean
  convertSystemPrompt?: boolean

  // NEW: optional agent voice configuration and optional response processor agent
  voice?: AgentVoiceConfig
  responseProcessorAgent?: string
}

export interface UserAgentConfig {
  type: 'user-agent'
  config: AgentConfig
}

export function isUserAgentConfig(config?: MergedModel): config is UserAgentConfig {
  return config != null && config.type === 'user-agent'
}

// Request for POST /v1/core/delete/agent
export interface DeleteAgentRequest {
  name: string // Agent name
}

export interface NewAgentConfig {
  name: string
  description: string
  icon?: string
  systemPrompt: string
  modelId: string // Internal ID of the linked model
  modelOptions: ModelOptions
  sendContextToAgent?: string
  toolOptions?: AgentToolOptions // Updated from tools to toolOptions
  embeddingOptions?: AgentEmbeddingOptions
  temperature?: number
  maxTokens?: number
  overwrite?: boolean
  addToday?: boolean           // Optional flag to add today's date to the prompt
  timezoneOffset?: string      // Optional timezone offset for addToday (based on UTC)
  combineSystemPrompts?: boolean
  convertSystemPrompt?: boolean

  // NEW
  voice?: AgentVoiceConfig
  responseProcessorAgent?: string
}

export interface NewAgentRequest extends Omit<NewAgentConfig, 'modelId' | 'modelOptions'> {
  model: string // User provides model *name*
  modelId?: string
  modelOptions?: ModelOptions
  toolOptions?: AgentToolOptions // Ensure NewAgentRequest also reflects this
}

export interface AgentMap {
  [key: string]: UserAgentConfig
}

export interface AgentConfigs {
  [key: string]: AgentConfig
}

export interface UserAgentConfigs {
  [key: string]: UserAgentConfig
}
